import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    Loader2, Save, DollarSign, ChevronDown, Check, Search,
    CircleDot, Play, Pause, XCircle, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { updatePriceRule, getGranulation } from '@/services/api';
import type { PriceRule } from '@/types/price-rules';

interface EditRuleModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rule: PriceRule | null;
    onSuccess?: () => void;
}

const STATUS_OPTIONS = [
    { value: 'active', label: 'Ativo', icon: Play, color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
    { value: 'paused', label: 'Pausado', icon: Pause, color: 'text-yellow-600', bg: 'bg-yellow-50', dot: 'bg-yellow-500' },
    { value: 'inactive', label: 'Inativo', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-500' },
];

export default function EditRuleModal({ open, onOpenChange, rule, onSuccess }: EditRuleModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [price, setPrice] = useState<number>(0);
    const [status, setStatus] = useState<string>('active');
    const [priceDropdownOpen, setPriceDropdownOpen] = useState(false);
    const [priceSearch, setPriceSearch] = useState('');
    const priceSearchRef = useRef<HTMLInputElement>(null);

    // Busca granulação de preços
    const { data: granulation } = useQuery({
        queryKey: ['granulation'],
        queryFn: getGranulation,
        staleTime: 1000 * 60 * 30, // 30 min cache
    });

    const priceValues: number[] = granulation?.values || [];

    // Inicializa com valores da regra selecionada
    useEffect(() => {
        if (rule && open) {
            setPrice(rule.price);
            setStatus(rule.status);
            setPriceSearch('');
            setPriceDropdownOpen(false);
        }
    }, [rule, open]);

    // Focus no search quando abre dropdown
    useEffect(() => {
        if (priceDropdownOpen && priceSearchRef.current) {
            setTimeout(() => priceSearchRef.current?.focus(), 50);
        }
    }, [priceDropdownOpen]);

    // Mutation pra atualizar
    const updateMutation = useMutation({
        mutationFn: (data: { price?: number; status?: string }) => {
            if (!rule) throw new Error('Regra não selecionada');
            return updatePriceRule(rule.id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['price-rules'] });
            toast({
                title: 'Regra atualizada',
                description: 'As alterações foram salvas com sucesso.',
            });
            onOpenChange(false);
            onSuccess?.();
        },
        onError: (error: any) => {
            toast({
                title: 'Erro ao atualizar',
                description: error?.response?.data?.detail || 'Não foi possível salvar as alterações.',
                variant: 'destructive',
            });
        },
    });

    const handleSave = () => {
        if (!rule) return;

        const changes: { price?: number; status?: string } = {};

        if (price !== rule.price) changes.price = price;
        if (status !== rule.status) changes.status = status;

        if (Object.keys(changes).length === 0) {
            toast({ title: 'Sem alterações', description: 'Nenhuma alteração foi feita.' });
            onOpenChange(false);
            return;
        }

        updateMutation.mutate(changes);
    };

    const hasChanges = rule ? (price !== rule.price || status !== rule.status) : false;

    const formatPrice = (val: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(val);

    // Filtra preços pelo search
    const filteredPrices = priceValues.filter(p => {
        if (!priceSearch) return true;
        const searchNum = priceSearch.replace(',', '.');
        return p.toString().includes(searchNum) || formatPrice(p).includes(priceSearch);
    });

    const getPmrStatus = (currentPmr: number | null, pmrTarget: number) => {
        if (currentPmr === null) return { icon: null, color: 'text-gray-400' };
        const diff = currentPmr - pmrTarget;
        if (diff >= 5) return { icon: <TrendingUp className="h-3.5 w-3.5" />, color: 'text-emerald-600' };
        if (diff <= -5) return { icon: <TrendingDown className="h-3.5 w-3.5" />, color: 'text-red-600' };
        return { icon: <Minus className="h-3.5 w-3.5" />, color: 'text-gray-500' };
    };

    if (!rule) return null;

    const pmrStatus = getPmrStatus(rule.current_pmr, rule.pmr_target);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-4">
                    <DialogTitle className="text-lg font-semibold">Editar Regra</DialogTitle>
                    <DialogDescription className="text-sm text-gray-500">
                        Altere o preço ou status da regra #{rule.id}
                    </DialogDescription>
                </DialogHeader>

                {/* Rule Info Summary */}
                <div className="mx-6 p-3 bg-gray-50 rounded-lg border border-gray-100 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-900">
                                    {rule.country === '*' ? '🌍 Todos' : rule.country}
                                </span>
                                <span className="text-gray-300">•</span>
                                <span className="text-gray-600">
                                    {rule.ad_unit === '*' ? 'Todos Blocos' : rule.ad_unit}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${
                                    rule.device === 'MOBILE' ? 'bg-blue-50 text-blue-700' :
                                        rule.device === 'WEB' ? 'bg-purple-50 text-purple-700' :
                                            'bg-gray-100 text-gray-600'
                                }`}>
                                    {rule.device === 'MOBILE' ? '📱 Mobile' : rule.device === 'WEB' ? '💻 Web' : '🌐 Todos'}
                                </span>
                                {rule.custom_key && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-orange-50 text-orange-700">
                                        {rule.custom_key}={rule.custom_value}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-right space-y-1">
                            <div className="flex items-center gap-1.5 justify-end">
                                <span className={pmrStatus.color}>{pmrStatus.icon}</span>
                                <span className="text-sm font-medium text-gray-900">
                                    PMR {rule.current_pmr !== null ? `${rule.current_pmr.toFixed(1)}%` : '-'}
                                </span>
                            </div>
                            <div className="text-xs text-gray-400">
                                eCPM {rule.current_ecpm ? formatPrice(rule.current_ecpm) : '-'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit Fields */}
                <div className="px-6 space-y-5">

                    {/* Price Field */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                            Preço
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setPriceDropdownOpen(!priceDropdownOpen)}
                                className={`w-full h-11 px-4 text-left text-sm font-medium border-2 rounded-xl bg-white flex items-center justify-between transition-colors ${
                                    priceDropdownOpen ? 'border-blue-500 ring-2 ring-blue-100' :
                                        price !== rule.price ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-base font-semibold text-gray-900">{formatPrice(price)}</span>
                                    {price !== rule.price && (
                                        <span className="text-xs text-gray-400 line-through">{formatPrice(rule.price)}</span>
                                    )}
                                </div>
                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${priceDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {priceDropdownOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => { setPriceDropdownOpen(false); setPriceSearch(''); }} />
                                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                        <div className="p-2 border-b border-gray-100">
                                            <div className="relative">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <input
                                                    ref={priceSearchRef}
                                                    type="text"
                                                    placeholder="Buscar preço..."
                                                    value={priceSearch}
                                                    onChange={(e) => setPriceSearch(e.target.value)}
                                                    className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-[200px] overflow-y-auto">
                                            {filteredPrices.length === 0 ? (
                                                <div className="px-4 py-3 text-sm text-gray-400 text-center">Nenhum preço encontrado</div>
                                            ) : (
                                                filteredPrices.map((p) => (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        onClick={() => { setPrice(p); setPriceDropdownOpen(false); setPriceSearch(''); }}
                                                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                            p === price ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                                        }`}
                                                    >
                                                        <span>{formatPrice(p)}</span>
                                                        {p === price && <Check className="h-4 w-4" />}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Quick price adjust */}
                        <div className="flex items-center gap-2">
                            {(() => {
                                const idx = priceValues.indexOf(price);
                                const prevPrice = idx > 0 ? priceValues[idx - 1] : null;
                                const nextPrice = idx >= 0 && idx < priceValues.length - 1 ? priceValues[idx + 1] : null;
                                return (
                                    <>
                                        <button
                                            type="button"
                                            disabled={!prevPrice}
                                            onClick={() => prevPrice && setPrice(prevPrice)}
                                            className="flex-1 h-8 text-xs font-medium border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                            ↓ {prevPrice ? formatPrice(prevPrice) : '-'}
                                        </button>
                                        <span className="text-xs text-gray-400 font-medium px-1">Step</span>
                                        <button
                                            type="button"
                                            disabled={!nextPrice}
                                            onClick={() => nextPrice && setPrice(nextPrice)}
                                            className="flex-1 h-8 text-xs font-medium border border-gray-200 rounded-lg hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                            ↑ {nextPrice ? formatPrice(nextPrice) : '-'}
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Status Field */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <CircleDot className="h-4 w-4 text-gray-400" />
                            Status
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {STATUS_OPTIONS.map((opt) => {
                                const isSelected = status === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setStatus(opt.value)}
                                        className={`relative h-11 flex items-center justify-center gap-2 text-sm font-medium rounded-xl border-2 transition-all ${
                                            isSelected
                                                ? `${opt.bg} ${opt.color} border-current`
                                                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className={`w-2 h-2 rounded-full ${isSelected ? opt.dot : 'bg-gray-300'}`} />
                                        {opt.label}
                                        {isSelected && (
                                            <Check className="h-3.5 w-3.5 absolute top-1.5 right-1.5 opacity-50" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 mt-5 bg-gray-50 border-t border-gray-100">
                    {/* Changes indicator */}
                    <div className="text-xs text-gray-400">
                        {hasChanges ? (
                            <span className="text-blue-600 font-medium">
                                {price !== rule.price && status !== rule.status ? 'Preço e status alterados' :
                                    price !== rule.price ? `Preço: ${formatPrice(rule.price)} → ${formatPrice(price)}` :
                                        `Status: ${rule.status} → ${status}`}
                            </span>
                        ) : (
                            'Sem alterações'
                        )}
                    </div>

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={updateMutation.isPending}
                            className="h-9"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!hasChanges || updateMutation.isPending}
                            className="h-9 bg-blue-600 hover:bg-blue-700"
                        >
                            {updateMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2 h-4 w-4" />
                                    Salvar
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}